import { apiRequest } from "./api";
import api from "./api";
import type {
  ServiceStaff,
  ServiceCategory,
  CalendarStaff,
  CalendarParams,
  AdminAppointmentListParams,
  AdminAppointmentListResponse,
  AppointmentDetails,
  Category,
  Service,
  Doctor,
  AvailableSlotsResponse,
  CreateAppointmentRequest,
  CreateAppointmentResponse,
  ConsoleCheckoutQuoteResponse,
  ReceptionCollectionSnapshot,
  ReceptionAwaitingPaymentResponse,
  ReceptionNotificationChannel,
  StaffAppointmentsResponse,
  PatientInfo,
  PatientConcern,
  PatientSurgery,
  PatientLifestyle,
  Activity,
  FamilyDiseaseHistory,
  PatientDiseaseHistory,
  LifestyleType,
  LifestyleFrequency,
  ActivityType,
  ActivityDuration,
  ActivityFrequency,
  Surgery,
  CreateDiseaseHistoryRequest,
  UpdateDiseaseHistoryRequest,
  DiseaseSource,
  DiseaseAppointmentSource,
  CreateFamilyDiseaseHistoryRequest,
  UpdateFamilyDiseaseHistoryRequest,
  CreatePatientLifestyleRequest,
  UpdatePatientLifestyleRequest,
  CreatePatientActivityRequest,
  UpdatePatientActivityRequest,
  CreatePatientSurgeryRequest,
  PatientSurgeryHistory,
  DiseaseListParams,
  LifestyleTypeListParams,
  ActivityTypeListParams,
  AppointmentHistory,
  PatientSymptom,
  Symptom,
  CreatePatientSymptomRequest,
  SymptomListParams,
  BloodPressure,
  BloodGlucose,
  HeartRate,
  Temperature,
  RespiratoryRate,
  OxygenSaturation,
  VitalsHomeResponse,
  BodyPosition,
  BodyLocation,
  SampleSource,
  SampleType,
  CreateBloodPressureRequest,
  CreateBloodGlucoseRequest,
  CreateHeartRateRequest,
  CreateTemperatureRequest,
  CreateRespiratoryRateRequest,
  CreateOxygenSaturationRequest,
  Disease,
  PatientAllergy,
  AllergyType,
  CreatePatientAllergyRequest,
  PatientMedicine,
  PatientDocument,
  Allergy,
  PastPrescription,
  InvestigationListParams,
  InvestigationItem,
  PatientInvestigationNote,
  CreateInvestigationNoteRequest,
  DiseaseSummary,
  ResolveCustomDiseaseRequest,
} from "@/types/appointments";
import type { ServiceModesResponse, TimeBlockClinic } from "@/types/clinics";

export const appointmentsAPI = {
  // Get all staff members grouped by category
  getServiceStaff: () =>
    apiRequest<ServiceStaff[]>(() => api.get("/categories/services/staff/")),

  // Get all services grouped by category
  getServices: () =>
    apiRequest<ServiceCategory[]>(() => api.get("/categories/services/")),

  // Get calendar data for appointments
  getCalendar: (params: CalendarParams) =>
    apiRequest<CalendarStaff[]>(() =>
      api.get("/services/calendar/", { params })
    ),

  // Get staff-admin appointment list
  getAdminAppointmentList: (params: AdminAppointmentListParams) =>
    apiRequest<AdminAppointmentListResponse>(() =>
      api.get("/internal/users/appointments/admin-list", { params })
    ),

  // Get appointment details by ID
  getAppointmentDetails: (appointmentId: string) =>
    apiRequest<AppointmentDetails>(() =>
      api.get(`/services/appointments/${appointmentId}/`)
    ),

  // Get all categories
  getCategories: () => apiRequest<Category[]>(() => api.get("/categories/")),

  // Get services by category
  getServicesByCategory: (categoryId: string) =>
    apiRequest<Service[]>(() => api.get(`/categories/${categoryId}/services/`)),

  // Get available booking modes for a service
  getServiceModes: (serviceId: string) =>
    apiRequest<ServiceModesResponse>(() =>
      api.get(`/services/${serviceId}/modes/`)
    ),

  // Get doctors by service (optional mode/clinic_id for clinic filtering)
  getDoctorsByService: (
    serviceId: string,
    params?: { mode?: string; clinic_id?: string }
  ) =>
    apiRequest<Doctor[]>(() =>
      api.get(`/services/${serviceId}/doctors/`, { params })
    ),

  // Get available slots for a doctor on a specific date
  getAvailableSlots: (
    serviceId: string,
    doctorId: string,
    date: string,
    params?: { mode?: string; clinic_id?: string }
  ) =>
    apiRequest<AvailableSlotsResponse>(() =>
      api.get(`/services/${serviceId}/doctors/${doctorId}/slot/`, {
        params: { date, ...params },
      })
    ),

  // Create/schedule an appointment
  createAppointment: (
    serviceId: string,
    doctorId: string,
    data: CreateAppointmentRequest
  ) =>
    apiRequest<CreateAppointmentResponse>(() =>
      api.post(
        `/services/${serviceId}/doctors/${doctorId}/slot/schedule/`,
        data,
        // A console booking confirms immediately, which sends SMS, WhatsApp and
        // Slack inside the request. The 10s default abandons a booking the
        // server goes on to create, and staff then book it twice.
        { timeout: 45000 }
      )
    ),

  getConsoleCheckoutQuote: (
    serviceId: string,
    doctorId: string,
    patientId: string,
    promoCode?: string
  ) =>
    apiRequest<ConsoleCheckoutQuoteResponse>(() =>
      api.post(`/services/${serviceId}/doctors/${doctorId}/checkout/quote/`, {
        patient_id: patientId,
        promo_code: promoCode ?? "",
      })
    ),

  getCollectionSnapshot: (appointmentId: string) =>
    apiRequest<ReceptionCollectionSnapshot>(() =>
      api.get(`/services/appointments/${appointmentId}/collection/`)
    ),

  sendCollectionNotification: (
    appointmentId: string,
    attemptId: string,
    channel: ReceptionNotificationChannel,
    email: string | undefined,
    idempotencyKey: string
  ) =>
    apiRequest<ReceptionCollectionSnapshot>(() =>
      api.post(
        `/services/appointments/${appointmentId}/collection/attempts/${attemptId}/notifications/`,
        { channel, ...(email ? { email } : {}) },
        { headers: { "Idempotency-Key": idempotencyKey } }
      )
    ),

  refreshCollection: (appointmentId: string, idempotencyKey: string) =>
    apiRequest<ReceptionCollectionSnapshot>(() =>
      api.post(
        `/services/appointments/${appointmentId}/collection/refresh/`,
        {},
        { headers: { "Idempotency-Key": idempotencyKey } }
      )
    ),

  cancelCollection: (appointmentId: string, idempotencyKey: string) =>
    apiRequest<ReceptionCollectionSnapshot>(() =>
      api.post(
        `/services/appointments/${appointmentId}/collection/cancel/`,
        {},
        { headers: { "Idempotency-Key": idempotencyKey } }
      )
    ),

  getAwaitingPayments: (page = 1, pageSize = 20) =>
    apiRequest<ReceptionAwaitingPaymentResponse>(() =>
      api.get("/services/appointments/collection/awaiting/", {
        params: { page, page_size: pageSize },
      })
    ),

  // Create time block
  createTimeBlock: (
    staffId: string,
    data: {
      staff_id: string;
      start_time: string;
      end_time: string;
      title?: string;
      note?: string;
      block_mode?: string;
      clinic?: string | null;
    }
  ) =>
    apiRequest<{
      id: string;
      user: string;
      start_time: string;
      end_time: string;
      title: string | null;
      note: string | null;
      block_mode?: string;
      clinic?: TimeBlockClinic | null;
      created_at: string;
      updated_at: string;
    }>(() =>
      api.post(`/users/time-blocks/`, data, { params: { staff_id: staffId } })
    ),

  // Staff appointments endpoints
  getStaffUpcomingAppointments: (page?: number) =>
    apiRequest<StaffAppointmentsResponse>(() =>
      api.get("/internal/users/appointments/staff/upcoming", {
        params: { page },
      })
    ),

  getStaffOngoingAppointments: (page?: number) =>
    apiRequest<StaffAppointmentsResponse>(() =>
      api.get("/internal/users/appointments/staff/ongoing", {
        params: { page },
      })
    ),

  getStaffHistoryAppointments: (page?: number) =>
    apiRequest<StaffAppointmentsResponse>(() =>
      api.get("/internal/users/appointments/staff/history", {
        params: { page },
      })
    ),

  // Cancel appointment
  cancelAppointment: (appointmentId: string) =>
    apiRequest<{ message: string }>(() =>
      api.delete(`/users/appointments/cancel/${appointmentId}/`)
    ),

  // Get patient info
  getPatientInfo: (userId: string, appointmentId: string) =>
    apiRequest<{ data: PatientInfo }>(() =>
      api.get(`/internal/users/${userId}/phr/${appointmentId}/info/`)
    ),

  // Get patient concerns
  getPatientConcerns: (appointmentId: string) =>
    apiRequest<PatientConcern[]>(() =>
      api.get(`/patient/${appointmentId}/concern/`)
    ),

  // Create patient concerns
  createPatientConcerns: (
    appointmentId: string,
    data: { concerns: string[]; is_active?: boolean }
  ) =>
    apiRequest<PatientConcern>(() =>
      api.post(`/patient/${appointmentId}/concern/`, data)
    ),

  // Update patient concerns
  updatePatientConcerns: (
    appointmentId: string,
    data: { concerns?: string[]; is_active?: boolean }
  ) =>
    apiRequest<PatientConcern>(() =>
      api.patch(`/patient/${appointmentId}/concern/`, data)
    ),

  // Get patient surgeries
  getPatientSurgeries: (appointmentId: string) =>
    apiRequest<PatientSurgery[]>(() =>
      api.get(`/patient/${appointmentId}/surgeries/`)
    ),

  // Get patient lifestyle
  getPatientLifestyle: (appointmentId: string) =>
    apiRequest<PatientLifestyle[]>(() =>
      api.get(`/patient/${appointmentId}/lifestyle/`)
    ),

  // Get patient activity
  getPatientActivity: (appointmentId: string) =>
    apiRequest<Activity[]>(() =>
      api.get(`/patient/${appointmentId}/activity/`)
    ),

  // Get patient family disease history
  getPatientFamilyDiseaseHistory: (patientId: string) =>
    apiRequest<FamilyDiseaseHistory[]>(() =>
      api.get(`/patient/patientfamilydisease/${patientId}`)
    ),

  // Get disease rows by source.
  // For source=intake_form (or omitted), `id` is patient_id (committed PatientDisease rows).
  // For source=prescription|meal_plan, `id` is appointment_id (AppointmentDiseaseDraftItem rows).
  getPatientDiseaseHistory: (id: string, source?: DiseaseSource) =>
    apiRequest<PatientDiseaseHistory[]>(() =>
      api.get(`/patient/${id}/disease/`, { params: { source } })
    ),

  // Initialise the appointment disease draft. Idempotent — returns existing draft items
  // on subsequent calls. Must be called before rendering prescription/meal-plan disease UI.
  initializeAppointmentDiseaseDraft: (
    appointmentId: string,
    source: DiseaseAppointmentSource
  ) =>
    apiRequest<PatientDiseaseHistory[]>(() =>
      api.post(`/patient/${appointmentId}/disease/initialize/`, { source })
    ),

  // Catalog APIs
  getDiseases: (params?: DiseaseListParams) =>
    apiRequest<Disease[]>(() => api.get("/diseases/", { params })),

  getLifestyleTypes: (params?: LifestyleTypeListParams) =>
    apiRequest<LifestyleType[]>(() => api.get("/lifestyles/type/", { params })),

  getLifestyleFrequencies: () =>
    apiRequest<LifestyleFrequency[]>(() => api.get("/lifestyles/frequency/")),

  getActivityTypes: (params?: ActivityTypeListParams) =>
    apiRequest<ActivityType[]>(() => api.get("/activities/type/", { params })),

  getActivityDurations: () =>
    apiRequest<ActivityDuration[]>(() => api.get("/activities/duration/")),

  getActivityFrequencies: () =>
    apiRequest<ActivityFrequency[]>(() => api.get("/activities/frequency/")),

  getSurgeries: () => apiRequest<Surgery[]>(() => api.get("/surgeries/")),

  // Disease CRUD.
  // For source=intake_form, `id` is patient_id (writes to committed PatientDisease).
  // For source=prescription|meal_plan, `id` is appointment_id (writes to draft items).
  createPatientDiseaseHistory: (
    id: string,
    data: CreateDiseaseHistoryRequest
  ) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.post(`/patient/${id}/disease/`, data)
    ),

  // Update a draft item. `id` is appointment_id; source disambiguates draft context.
  updatePatientDiseaseHistory: (
    id: string,
    rowId: string,
    data: UpdateDiseaseHistoryRequest,
    source?: DiseaseSource
  ) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.patch(`/patient/${id}/disease/${rowId}/`, data, {
        params: { source },
      })
    ),

  // Delete an appointment-added disease row. For prescription past rows, use
  // excludeFromPrescription instead — this DELETE will be rejected by BE.
  deletePatientDiseaseHistory: (
    id: string,
    rowId: string,
    source?: DiseaseSource
  ) =>
    apiRequest<void>(() =>
      api.delete(`/patient/${id}/disease/${rowId}/`, { params: { source } })
    ),

  getDiseaseSummary: (patientId: string) =>
    apiRequest<DiseaseSummary>(() =>
      api.get(`/patient/${patientId}/disease-summary/`)
    ),

  confirmDiseaseHistory: (appointmentId: string, draftItemId: string) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.post(
        `/patient/${appointmentId}/disease-review/${draftItemId}/confirm/`
      )
    ),

  rejectDiseaseHistory: (appointmentId: string, draftItemId: string) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.post(
        `/patient/${appointmentId}/disease-review/${draftItemId}/reject/`
      )
    ),

  deactivateDiseaseHistory: (appointmentId: string, draftItemId: string) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.post(
        `/patient/${appointmentId}/disease-review/${draftItemId}/deactivate/`
      )
    ),

  // Required path for removing a past row from prescription context.
  excludeFromPrescription: (appointmentId: string, draftItemId: string) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.post(
        `/patient/${appointmentId}/disease-review/${draftItemId}/exclude-from-prescription/`
      )
    ),

  // Symmetric counterpart to excludeFromPrescription. Required to restore an
  // excluded past prescription row; PATCH activity_status:"active"
  // is rejected by BE for these rows by design.
  includeInPrescription: (appointmentId: string, draftItemId: string) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.post(
        `/patient/${appointmentId}/disease-review/${draftItemId}/include-in-prescription/`
      )
    ),

  resolveCustomDisease: (
    appointmentId: string,
    draftItemId: string,
    data: ResolveCustomDiseaseRequest
  ) =>
    apiRequest<PatientDiseaseHistory>(() =>
      api.post(
        `/patient/${appointmentId}/custom-disease-review/${draftItemId}/resolve/`,
        data
      )
    ),

  rejectCustomDisease: (appointmentId: string, draftItemId: string) =>
    apiRequest<void>(() =>
      api.post(
        `/patient/${appointmentId}/custom-disease-review/${draftItemId}/reject/`
      )
    ),

  // Family Disease History CRUD
  createFamilyDiseaseHistory: (data: CreateFamilyDiseaseHistoryRequest) =>
    apiRequest<FamilyDiseaseHistory>(() =>
      api.post("/patient/patientfamilydisease/", data)
    ),

  updateFamilyDiseaseHistory: (
    historyId: string,
    data: UpdateFamilyDiseaseHistoryRequest
  ) =>
    apiRequest<FamilyDiseaseHistory>(() =>
      api.patch(`/patient/patientfamilydisease/${historyId}/`, data)
    ),

  deleteFamilyDiseaseHistory: (historyId: string) =>
    apiRequest<void>(() =>
      api.delete(`/patient/patientfamilydisease/${historyId}/`)
    ),

  // Patient Lifestyle CRUD
  createPatientLifestyle: (
    patientId: string,
    data: CreatePatientLifestyleRequest
  ) =>
    apiRequest<PatientLifestyle>(() =>
      api.post(`/patient/${patientId}/lifestyle/`, data)
    ),

  updatePatientLifestyle: (
    patientId: string,
    lifestyleId: string,
    data: UpdatePatientLifestyleRequest
  ) =>
    apiRequest<PatientLifestyle>(() =>
      api.patch(`/patient/${patientId}/lifestyle/${lifestyleId}/`, data)
    ),

  deletePatientLifestyle: (patientId: string, lifestyleId: string) =>
    apiRequest<void>(() =>
      api.delete(`/patient/${patientId}/lifestyle/${lifestyleId}/`)
    ),

  // Patient Activity CRUD
  createPatientActivity: (
    appointmentId: string,
    data: CreatePatientActivityRequest
  ) =>
    apiRequest<Activity>(() =>
      api.post(`/patient/${appointmentId}/activity/`, data)
    ),

  updatePatientActivity: (
    appointmentId: string,
    activityId: string,
    data: UpdatePatientActivityRequest
  ) =>
    apiRequest<Activity>(() =>
      api.patch(`/patient/${appointmentId}/activity/${activityId}/`, data)
    ),

  deletePatientActivity: (appointmentId: string, activityId: string) =>
    apiRequest<void>(() =>
      api.delete(`/patient/${appointmentId}/activity/${activityId}/`)
    ),

  // Patient Surgery CRUD
  createPatientSurgery: (
    appointmentId: string,
    data: CreatePatientSurgeryRequest
  ) =>
    apiRequest<PatientSurgeryHistory>(() =>
      api.post(`/patient/${appointmentId}/surgeries/`, data)
    ),

  deletePatientSurgery: (appointmentId: string, surgeryId: string) =>
    apiRequest<void>(() =>
      api.delete(`/patient/${appointmentId}/surgeries/${surgeryId}/`)
    ),

  // Get appointment history for a patient
  getAppointmentHistory: (
    patientId: string,
    status: "completed" = "completed"
  ) => {
    const params: { status: "completed"; patient_id?: string } = { status };
    if (patientId.trim()) {
      params.patient_id = patientId;
    }

    return apiRequest<AppointmentHistory[]>(() =>
      api.get("/users/appointments/history", {
        params,
      })
    );
  },

  // Symptoms APIs
  createPatientSymptom: (data: CreatePatientSymptomRequest) =>
    apiRequest<PatientSymptom>(() =>
      api.post("/patient/patientsymptoms/", data)
    ),

  getPatientSymptoms: (appointmentId: string) =>
    apiRequest<PatientSymptom[]>(() =>
      api.get(`/patient/patientsymptoms/${appointmentId}/`)
    ),

  // Symptom drafts use `activity_status` (active/inactive) for soft-delete.
  // The backend write serializer ignores `is_active`, so we must PATCH
  // `activity_status: "inactive"` for the draft item to be deactivated.
  deletePatientSymptom: (symptomId: string) =>
    apiRequest<PatientSymptom>(() =>
      api.patch(`/patient/patientsymptoms/${symptomId}/`, {
        activity_status: "inactive",
      })
    ),

  getSymptoms: (params?: SymptomListParams) =>
    apiRequest<Symptom[]>(() => api.get("/symptom/", { params })),

  // Vitals APIs
  createBloodPressure: (
    appointmentId: string,
    data: CreateBloodPressureRequest
  ) =>
    apiRequest<BloodPressure>(() =>
      api.post(`/vitals/${appointmentId}/blood-pressure/`, data)
    ),

  createBloodGlucose: (
    appointmentId: string,
    data: CreateBloodGlucoseRequest
  ) =>
    apiRequest<BloodGlucose>(() =>
      api.post(`/vitals/${appointmentId}/blood-glucose/`, data)
    ),

  createHeartRate: (appointmentId: string, data: CreateHeartRateRequest) =>
    apiRequest<HeartRate>(() =>
      api.post(`/vitals/${appointmentId}/heart-beat/`, data)
    ),

  createTemperature: (appointmentId: string, data: CreateTemperatureRequest) =>
    apiRequest<Temperature>(() =>
      api.post(`/vitals/${appointmentId}/temperature/`, data)
    ),

  createRespiratoryRate: (
    appointmentId: string,
    data: CreateRespiratoryRateRequest
  ) =>
    apiRequest<RespiratoryRate>(() =>
      api.post(`/vitals/${appointmentId}/respiratory-rate/`, data)
    ),

  createOxygenSaturation: (
    appointmentId: string,
    data: CreateOxygenSaturationRequest
  ) =>
    apiRequest<OxygenSaturation>(() =>
      api.post(`/vitals/${appointmentId}/oxygen-saturation/`, data)
    ),

  getVitalsHome: (appointmentId: string) =>
    apiRequest<VitalsHomeResponse>(() =>
      api.get(`/vitals/${appointmentId}/home/`)
    ),

  // Vitals Helper APIs
  getBloodPressurePositions: () =>
    apiRequest<BodyPosition[]>(() =>
      api.get("/vitals/blood-pressure-positions/")
    ),

  getBloodPressureLocations: () =>
    apiRequest<BodyLocation[]>(() =>
      api.get("/vitals/blood-pressure-locations/")
    ),

  getTemperatureLocations: () =>
    apiRequest<BodyLocation[]>(() => api.get("/vitals/temperature-locations/")),

  getBloodGlucoseSampleSources: () =>
    apiRequest<SampleSource[]>(() =>
      api.get("/vitals/blood-glucose-source-name/")
    ),

  getBloodGlucoseSampleTypes: () =>
    apiRequest<SampleType[]>(() =>
      api.get("/vitals/blood-glucose-source-type/")
    ),

  // Medicines & Allergies APIs
  getPatientAllergies: (appointmentId: string) =>
    apiRequest<PatientAllergy[]>(() =>
      api.get(`/medicines/${appointmentId}/allergy/`)
    ),

  addPatientAllergy: (
    appointmentId: string,
    data: CreatePatientAllergyRequest
  ) =>
    apiRequest<PatientAllergy>(() =>
      api.post(`/medicines/${appointmentId}/allergy/`, data)
    ),

  getAllergyTypes: () =>
    apiRequest<AllergyType[]>(() => api.get("/medicines/allergy-type/")),

  getAllergies: (search: string) =>
    apiRequest<Allergy[]>(() =>
      api.get("/medicines/allergy/", { params: { search } })
    ),

  deletePatientAllergy: (appointmentId: string, allergyId: string) =>
    apiRequest<void>(() =>
      api.delete(`/medicines/${appointmentId}/allergy/${allergyId}/`)
    ),

  getPatientMedicines: (appointmentId: string) =>
    apiRequest<PatientMedicine>(() =>
      api.get(`/patient/${appointmentId}/medicine/`)
    ),

  getPatientDocuments: (patientId: string, consolidated: boolean = false) =>
    apiRequest<PatientDocument[]>(() =>
      api.get(`/patient/${patientId}/document/`, { params: { consolidated } })
    ),

  getPastPrescriptions: (appointmentId: string) =>
    apiRequest<PastPrescription[]>(() =>
      api.get(`/medicines/${appointmentId}/prescription/`, {
        params: { consolidated: true },
      })
    ),

  // Investigation APIs
  getInvestigations: (params?: InvestigationListParams) =>
    apiRequest<InvestigationItem[]>(() =>
      api.get("/investigations/", { params })
    ),

  getPatientInvestigationNotes: (
    appointmentId: string,
    consolidated: boolean = false
  ) =>
    apiRequest<PatientInvestigationNote[]>(() =>
      api.get(`/patient/${appointmentId}/investigation/`, {
        params: { consolidated },
      })
    ),

  createPatientInvestigationNote: (
    appointmentId: string,
    data: CreateInvestigationNoteRequest
  ) =>
    apiRequest<PatientInvestigationNote>(() =>
      api.post(`/patient/${appointmentId}/investigation/`, data)
    ),

  updatePatientInvestigationNote: (
    appointmentId: string,
    noteId: string,
    data: { value: number }
  ) =>
    apiRequest<PatientInvestigationNote>(() =>
      api.patch(`/patient/${appointmentId}/investigation/${noteId}/`, data)
    ),

  deletePatientInvestigationNote: (appointmentId: string, noteId: string) =>
    apiRequest<void>(() =>
      api.delete(`/patient/${appointmentId}/investigation/${noteId}/`)
    ),
};
